import { Button, ButtonSize, ButtonType } from '@/common/button';
import { Modal } from '@/common/modal';
import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState, useRef } from 'react';
import type { ChangeEvent, FC } from 'react';

interface DynamicDataTableModalProps {
    dataColumns: string[];
    dataRows: string[][];
    setDataRows: (dataRows: string[][]) => void;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}

export const DynamicDataTableModal: FC<DynamicDataTableModalProps> = ({
    dataColumns,
    dataRows,
    setDataRows,
    isOpen,
    setIsOpen,
}) => {
    const [rows, setRows] = useState([...dataRows, new Array(dataColumns.length).fill('')]);

    const tableBodyRef = useRef<HTMLTableSectionElement>(null);
    const dynamicWidth = Math.min(dataColumns.length * 20, 50);

    useEffect(() => {
        if (rows.length < 10) {
            setRows((prevRows) => {
                const newRows = [...prevRows];
                const rowsToAdd = 10 - newRows.length;
                for (let i = 0; i < rowsToAdd; i++) {
                    newRows.push(new Array(dataColumns.length).fill(''));
                }
                return newRows;
            });
        }
    }, [dataColumns.length, rows.length]);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>, rowIndex: number, colIndex: number) => {
        const value = e.target.value;
        setRows((prevRows) => {
            const newRows = [...prevRows];
            newRows[rowIndex] = [...newRows[rowIndex]];
            newRows[rowIndex][colIndex] = value;

            if (rowIndex === newRows.length - 1 && newRows[rowIndex].every((cell) => cell !== '')) {
                newRows.push(new Array(dataColumns.length).fill(''));
            }
            return newRows;
        });
    };

    const handleSave = () => {
        setDataRows(rows.filter((row) => row.some((cell) => cell.trim() !== '')));
        setIsOpen(false);
    };

    return (
        <Modal isOpen={isOpen}>
            <div
                className="flex flex-col absolute bg-gray-150 shadow-xl rounded-xl transition-all duration-300 ease-in-out overflow-hidden"
                style={{ width: `${dynamicWidth}vw`, maxWidth: '50vw', maxHeight: '80vh' }}
            >
                <div className="flex flex-row justify-between p-4 items-center bg-gray-900 rounded-t-xl">
                    <p className="text-white text-xl">Add Bulk Content</p>
                    <XMarkIcon
                        className="h-5 w-5 text-white hover:cursor-pointer hover:text-gray-30"
                        onClick={() => setIsOpen(false)}
                    />
                </div>
                <hr className="bg-gray-100 border-gray-120" />
                <div className="overflow-x-auto max-w-full">
                    <div className="overflow-y-auto p-3 no-scrollbar" style={{ maxHeight: '60vh' }}>
                        <table className="border text-center border-gray-100 border-collapse min-w-max w-full rounded-lg overflow-hidden outline outline-1 outline-gray-100">
                            <thead className="bg-gray-100 rounded-t-lg">
                                <tr>
                                    <th className="p-2 rounded-tl-lg border text-white border-gray-100" />
                                    {dataColumns.map((col, index) => (
                                        <th
                                            className={`w-[20vw] text-white text-sm font-normal text-left p-2 border border-gray-100 ${
                                                index === dataColumns.length - 1 ? 'rounded-tr-lg' : ''
                                            }`}
                                            key={col}
                                        >
                                            {col}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody ref={tableBodyRef} className="bg-gray-800">
                                {rows.map((row, rowIndex) => (
                                    <tr key={`row-${rowIndex + 1}`}>
                                        <td className="text-xs text-white font-normal border border-gray-100 px-2">
                                            {rowIndex + 1}.
                                        </td>
                                        {row.map((cell, colIndex) => (
                                            <td className="border border-gray-100 px-2" key={`col-${colIndex + 1}`}>
                                                <input
                                                    type="text"
                                                    className="w-full bg-transparent text-white border-none outline-none p-1"
                                                    value={cell}
                                                    onChange={(e) => handleInputChange(e, rowIndex, colIndex)}
                                                />
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <hr className="bg-gray-100 border-gray-120" />
                <div className="w-full flex flex-row justify-end gap-3 p-6">
                    <Button
                        title="Cancel"
                        className="hover:bg-gray-160"
                        buttonType={ButtonType.SECONDARY}
                        buttonSize={ButtonSize.LARGE}
                        onClick={() => setIsOpen(false)}
                    />
                    <Button
                        title="Yes"
                        buttonType={ButtonType.PRIMARY}
                        buttonSize={ButtonSize.LARGE}
                        onClick={handleSave}
                    />
                </div>
            </div>
        </Modal>
    );
};
